import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge, Box, Heading, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { getBeneficiarySuperMerchants } from 'api/beneficiaries';
import { SUPER_MERCHANT_BENEFICIARIES_ROUTE } from 'constants/routes';
import { SUPER_MERCHANT_TABLE_HEADINGS } from 'modules/beneficiaries/constants/constants';
import { formatBeneficiaryStatus } from 'modules/beneficiaries/utils/utils';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';
import Search from 'components/SearchBox/Search';

const SuperMerchantList = () => {
  const history = useHistory();

  const [superMerchants, setSuperMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchSuperMerchants = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getBeneficiarySuperMerchants();

        setSuperMerchants(response?.data?.data ?? []);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSuperMerchants();
  }, []);

  const filteredSuperMerchants = useMemo(() => {
    if (!searchTerm) return superMerchants;

    const term = searchTerm.toLowerCase();

    return superMerchants?.filter(
      (superMerchant) =>
        superMerchant?.name?.toLowerCase().includes(term) ||
        superMerchant?.id?.toLowerCase().includes(term) ||
        superMerchant?.code?.toLowerCase().includes(term)
    );
  }, [superMerchants, searchTerm]);

  const handleRowClick = (superMerchant) => {
    history.push(SUPER_MERCHANT_BENEFICIARIES_ROUTE(superMerchant?.id), {
      superMerchantName: superMerchant?.name,
    });
  };

  return (
    <Box overflowX='auto' maxWidth='100%' p={4} borderRadius='lg' boxShadow='sm' bg='white'>
      <Heading as='h1' size='md' mb={3} mt={4} color='gray.700'>
        Beneficiaries
        {!!filteredSuperMerchants?.length && (
          <Badge colorScheme='gray' rounded='full' px={2.5} py={0.5} ml={2}>
            {filteredSuperMerchants?.length}
          </Badge>
        )}
      </Heading>

      <Search searchType='Super Merchant' setSearchTerm={setSearchTerm} />

      <LoadingErrorWrapper isLoading={loading} errorTitle='Error fetching super merchants' error={error}>
        <TableContainer borderRadius='lg' border='1px solid' borderColor='gray.200' overflowX='auto' mt={4}>
          <Table variant='simple' size='md'>
            <Thead bg='gray.100' position='sticky' top={0} zIndex={1}>
              <Tr>
                {SUPER_MERCHANT_TABLE_HEADINGS.map((heading, idx) => (
                  <Th key={idx} fontSize='sm' fontWeight='bold' color='gray.700' py={3}>
                    {heading}
                  </Th>
                ))}
              </Tr>
            </Thead>
            {!!filteredSuperMerchants?.length && (
              <Tbody>
                {filteredSuperMerchants?.map((superMerchant) => (
                  <Tr
                    key={superMerchant?.id}
                    fontSize='sm'
                    onClick={() => handleRowClick(superMerchant)}
                    cursor='pointer'
                    _hover={{ bg: 'gray.50' }}
                  >
                    <Td color='gray.600' fontWeight='bold'>
                      {superMerchant?.name ?? '-'}
                    </Td>
                    <Td color='gray.600'>{superMerchant?.id ?? '-'}</Td>
                    <Td color='gray.600'>{superMerchant?.code || '-'}</Td>
                    <Td color='gray.600'>{superMerchant?.country_code || '-'}</Td>
                    <Td color='gray.600'>{formatBeneficiaryStatus(superMerchant?.status)}</Td>
                  </Tr>
                ))}
              </Tbody>
            )}
          </Table>
        </TableContainer>
        {!filteredSuperMerchants?.length && (
          <Box textAlign='center' mt={4}>
            <Heading size='md' color='gray.500'>
              No Super Merchants found
            </Heading>
          </Box>
        )}
      </LoadingErrorWrapper>
    </Box>
  );
};

export default SuperMerchantList;
